import React from 'react'

import { Box, Typography, useMediaQuery } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import { useTheme } from '@mui/material/styles'

import { navigate, useLocation } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { ADMIN_NAV_COUNTS_QUERY } from 'src/components/Message/supportQueries'

interface MenuOption {
  icon: React.ComponentType<any>
  title: string
  url?: string
  onClick?: () => unknown
  /** Treat item as active for icon color + list selection (e.g. Packning subpaths). */
  activeMatch?: (pathname: string) => boolean
  /**
   * Non-admin: when this row is selected, keep icon outline in accent orange and
   * only the inner stroke dark (e.g. Packning box tape line).
   */
  keepAccentOutlineWhenActive?: boolean
  badgeKey?:
    | 'messagesToHandle'
    | 'inquiriesToHandle'
    | 'packingDiscrepanciesToHandle'
}

interface Props {
  list: MenuOption[]
  mobileOpen: boolean
  handleDrawerToggle: () => void
  window?: () => Window
}

const drawerWidth = 254
const NAV_COUNTS_POLL_MS = 15000

const CountCircle = ({ count }: { count: number }) => {
  if (count <= 0) return null
  return (
    <Box
      component="span"
      sx={{
        ml: 'auto',
        width: 22,
        height: 22,
        borderRadius: '50%',
        bgcolor: '#e55a3e',
        color: 'common.white',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {count > 99 ? '99+' : count}
    </Box>
  )
}

const AdminPanel = ({
  list,
  window,
  mobileOpen,
  handleDrawerToggle,
}: Props) => {
  const location = useLocation()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))
  const { hasRole } = useAuth()
  const isAdmin = hasRole('admin')

  const { data: countsData } = useQuery(ADMIN_NAV_COUNTS_QUERY, {
    skip: !isAdmin,
    pollInterval: NAV_COUNTS_POLL_MS,
    errorPolicy: 'all',
  })

  const counts = countsData?.adminNavCounts as
    | {
        messagesToHandle: number
        inquiriesToHandle: number
        packingDiscrepanciesToHandle: number
      }
    | undefined

  const drawer = (
    <>
      <Box sx={{ overflow: 'auto', marginTop: 8 }}>
        <List>
          {list.map((list) => {
            const pathname = location.pathname
            const isActive =
              !!list.url &&
              (pathname === list.url || list.activeMatch?.(pathname) === true)
            const inactiveIconColor = hasRole('admin') ? '#2D3748' : '#F6A237'
            const iconColor = isActive ? '#2D3748' : inactiveIconColor
            const splitAccentOutline =
              list.keepAccentOutlineWhenActive && isActive && !hasRole('admin')
            const resolvedIconColor = splitAccentOutline
              ? inactiveIconColor
              : iconColor
            const badgeCount = list.badgeKey
              ? (counts?.[list.badgeKey] ?? 0)
              : 0
            return (
              <ListItem
                key={list.title}
                onClick={() => {
                  handleDrawerToggle()
                  if (list.url) navigate(list.url)
                  if (list.onClick) list.onClick()
                }}
                button
                selected={isActive}
                sx={{
                  color: 'rgba(0, 0, 0, 0.87)',
                  borderRadius: 2,
                  // '&.Mui-selected': {
                  //   bgcolor: 'pick-a-color',
                  // },
                  //I dont think the below is working. Think it should be Mui-selected and bgcolor
                  '&.active': {
                    backgroundColor: '150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                  },
                }}
              >
                <Box sx={{ mr: 2, color: resolvedIconColor, display: 'flex' }}>
                  <list.icon
                    color={resolvedIconColor}
                    {...(splitAccentOutline ? { accentColor: '#2D3748' } : {})}
                  />
                </Box>
                {/* <ListItemIcon>{list.icon}</ListItemIcon> */}
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        component="span"
                        variant="body1"
                        noWrap
                        sx={{ color: 'inherit' }}
                      >
                        {list.title}
                      </Typography>
                      <CountCircle count={badgeCount} />
                    </Box>
                  }
                />
              </ListItem>
            )
          })}
        </List>
      </Box>
    </>
  )

  const container =
    window !== undefined ? () => window().document.body : undefined

  return (
    <>
      <Drawer
        container={container}
        variant={isSmall ? 'temporary' : 'permanent'}
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': { width: drawerWidth, border: 0 },
        }}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        {drawer}
      </Drawer>
    </>
  )
}

export default AdminPanel
